<script lang="ts">
	/**
	 * The bottom tab bar: الرئيسية, المصحف, الصلاة, العلامات.
	 *
	 * ═══════════════════════════════════════════════════════════════════════
	 *  THE CONTRACT
	 * ═══════════════════════════════════════════════════════════════════════
	 *   - FOUR TABS, AND IT STAYS FOUR. A fifth is what turns a product into a
	 *     menu, and the fifth is always the one somebody adds in a hurry.
	 *     Settings hangs off Home's header, not off this bar.
	 *   - RENDERED ONCE, in `+layout.svelte`, after `<main>` and the
	 *     mini-player, so the `<nav>` landmark sits outside `<main>` and the bar
	 *     persists across navigation. `tests/ui/nav-structure.spec.ts` holds
	 *     that: a second copy would also duplicate `view-transition-name:
	 *     tab-bar` and abort every route transition.
	 *   - THE LIT TAB IS `nav.tab`, the tab the current screen belongs to (the
	 *     shared screens inherit the one they were opened from; see
	 *     `nav-model.ts`). `aria-current="page"` on that tab's root,
	 *     `aria-current="true"` on its sub-pages. The pill, the filled glyph,
	 *     the 600-weight accent label and `aria-current` carry it together.
	 *   - A TAP ON THE LIT TAB at its root scrolls to the top and does not
	 *     navigate; on one of its sub-pages it is a link up to the root. The
	 *     Read tab's href is the continue point while another tab is lit, and
	 *     the surah index while المصحف is: inside the reader it goes UP instead
	 *     of reloading the ayah already on screen.
	 *   - THE INDICATOR GROWS IN PLACE, from its own centre (`scaleX`), so it
	 *     needs no direction and is RTL-proof. The bar persists, so it plays on
	 *     every tab change, with or without view transitions; during one, the
	 *     bar's live image stays still and the grow plays inside it.
	 *
	 * ═══════════════════════════════════════════════════════════════════════
	 *  THE MEASURED SPACER
	 * ═══════════════════════════════════════════════════════════════════════
	 * The bar is `position: fixed`, and it renders an in-flow spacer so no page
	 * ever has to know how tall it is: a bar covering the last ayah of an-Nas is
	 * the bug a constant buys. Its height is CONTENT-DRIVEN (the labels follow
	 * `--type-scale`, and the face swap changes every metric), so the bar
	 * MEASURES its list and publishes `--tab-bar-content` on `<html>`. The
	 * spacer reads it back, and so does `app.css`'s `--tab-bar-block`, which
	 * every bottom-anchored island (the mini-player, the reader's display
	 * button, the ayah bar) builds on. The bar has no border, so nobody adds
	 * `+ 1px`.
	 *
	 * The safe-area inset is deliberately NOT in the measurement: `<body>`
	 * already pads `env(safe-area-inset-bottom)` and the spacer is in flow
	 * inside it, so counting it twice would leave a blank band. The fixed
	 * `<nav>` pads it itself, because body padding does not reach it.
	 */

	import { page } from '$app/state';
	import { onMount } from 'svelte';

	import Icon, { type IconName } from '$lib/ui/icons/Icon.svelte';
	import { prefersReducedMotion } from '$lib/ui/theme';

	import { bookmarksHref, continueHref, homeHref, readIndexHref, salahHref } from './links';
	import { TAB_ROOTS, type TabId } from './nav-model';
	import { nav } from './nav.svelte';

	interface Tab {
		id: TabId;
		label: string;
		icon: IconName;
		href: string;
	}

	/* `$derived`: the Read tab's href follows the reading place and the lit
	 * tab. */
	let tabs = $derived<readonly Tab[]>([
		{ id: 'home', label: 'الرئيسية', icon: 'home', href: homeHref() },
		{
			id: 'read',
			label: 'المصحف',
			icon: 'book',
			href: nav.tab === 'read' ? readIndexHref() : continueHref(nav.readingPlace)
		},
		{ id: 'salah', label: 'الصلاة', icon: 'clock', href: salahHref() },
		{ id: 'bookmarks', label: 'العلامات', icon: 'bookmark', href: bookmarksHref() }
	]);

	function currentFor(id: TabId): 'page' | 'true' | undefined {
		if (page.route.id === TAB_ROOTS[id]) return 'page';
		return nav.tab === id ? 'true' : undefined;
	}

	function onTabClick(event: MouseEvent, tab: Tab): void {
		if (event.defaultPrevented || event.button !== 0) return;
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
		if (nav.tab !== tab.id || page.route.id !== TAB_ROOTS[tab.id]) return;
		event.preventDefault();
		window.scrollTo({ top: 0, behavior: prefersReducedMotion(window) ? 'instant' : 'smooth' });
	}

	let listEl = $state<HTMLElement | undefined>(undefined);

	/** The one place the measurement is written. */
	function publishHeight(): void {
		if (typeof document === 'undefined' || !listEl) return;
		const height = listEl.getBoundingClientRect().height;
		if (height > 0) {
			document.documentElement.style.setProperty('--tab-bar-content', `${height}px`);
		}
	}

	/*
	 * RE-MEASURE ON EVERY RENDER AND WHEN THE FACE ARRIVES, not only on the
	 * `ResizeObserver`.
	 *
	 * The observer is the right mechanism and it is not sufficient on its own: a
	 * window that is occluded or minimised runs no rendering steps, so it
	 * delivers no `ResizeObserver` callbacks at all (measured: zero in 800 ms,
	 * while `visibilityState` still said "visible"). In that state the bar would
	 * keep publishing its MOUNT-TIME height forever — and the mount-time height
	 * is the wrong one, because the bar grows when Amiri replaces the fallback
	 * and when the labels change.
	 *
	 * Measured symptom before this: the spacer sat at 61 px while the bar was
	 * 86 px, so 25 px of the document stayed underneath it — a smaller version of
	 * exactly the bug the measured spacer was introduced to fix.
	 */
	$effect(() => {
		void [tabs, nav.tab, page.route.id];
		publishHeight();
	});

	/* Measure the bar's CONTENT height and publish it; the spacer reads it back.
	 * The list and not the `<nav>`, because the `<nav>` also carries the
	 * safe-area padding, which the spacer must not count a second time. The
	 * property is removed only on destroy, which for a bar mounted once in the
	 * layout means a hot reload. */
	onMount(() => {
		if (!listEl) return;
		const observer = new ResizeObserver(publishHeight);
		publishHeight();
		observer.observe(listEl);
		/* And once the mushaf face lands, because it changes every metric in the
		 * bar. `font-display: swap` on the UI faces means this genuinely happens
		 * after mount. */
		if ('fonts' in document) void document.fonts.ready.then(publishHeight);
		return () => {
			observer.disconnect();
			document.documentElement.style.removeProperty('--tab-bar-content');
		};
	});
</script>

<!-- In flow, so the document ends above the bar rather than under it. -->
<div class="spacer" aria-hidden="true"></div>

<nav class="tabs" aria-label="التنقل الرئيسي">
	<!--
		`role="list"` is REDUNDANT IN CHROME AND FIREFOX AND LOAD-BEARING IN
		SAFARI. WebKit strips list semantics from any list whose computed
		`list-style-type` is `none` — and the rule below sets exactly that — so on
		iOS VoiceOver, the primary platform for an installable PWA, this stops
		being a list at all and the item count is never announced.
	-->
	<ul bind:this={listEl} role="list">
		{#each tabs as tab (tab.id)}
			{@const current = currentFor(tab.id)}
			<li>
				<a
					href={tab.href}
					data-nav="tab"
					data-tab={tab.id}
					aria-current={current}
					onclick={(e) => onTabClick(e, tab)}
				>
					<span class="pill" aria-hidden="true"
						><Icon name={tab.icon} filled={current !== undefined} /></span
					>
					<span class="label">{tab.label}</span>
				</a>
			</li>
		{/each}
	</ul>
</nav>

<style>
	/*
	 * The spacer is the bar's MEASURED content height, with a constant standing
	 * in until the first measurement lands. The safe-area inset is NOT here —
	 * `body` already carries it. See the header.
	 */
	.spacer {
		flex: none;
		block-size: var(--tab-bar-content, 3.75rem);
	}

	/* On the page's own ground (`--bg`, which is also `theme-color`), so it
	   reads as part of the screen and not as a white footer card. The hairline
	   is a shadow, not a border, so the measured list is the whole content. */
	.tabs {
		position: fixed;
		inset-block-end: 0;
		inset-inline: 0;
		z-index: 10;
		background-color: var(--bg);
		box-shadow: 0 calc(-1 * var(--hairline)) 0 var(--chrome-edge);
		padding-block-end: env(safe-area-inset-bottom, 0px);
		view-transition-name: tab-bar;
	}

	ul {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		list-style: none;
		margin-block: 0;
		margin-inline: auto;
		padding-block: 0;
		padding-inline: var(--space-2);
		max-inline-size: 30rem;
	}

	/*
	 * CAPPED, unlike everything else in this app, and deliberately: navigation
	 * furniture is not the document. A 24 px glyph in a 56×32 pill and a 12 px
	 * label at the default size, growing to 28 px and 15 px and stopping there.
	 */
	a {
		--tab-icon: min(calc(1.5rem * var(--type-scale)), 1.75rem);
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.25rem;
		min-block-size: 3.25rem;
		padding-block: 0.375rem 0.25rem;
		color: var(--fg-muted);
		font-size: min(calc(0.75rem * var(--type-scale)), 0.9375rem);
		font-weight: 500;
		line-height: 1.25;
		text-decoration: none;
		-webkit-touch-callout: none;
		-webkit-user-select: none;
		user-select: none;
		transition: color var(--duration-base) var(--ease);
	}

	.label {
		max-inline-size: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.pill {
		position: relative;
		isolation: isolate;
		display: grid;
		place-items: center;
		inline-size: calc(var(--tab-icon) + 2rem);
		block-size: calc(var(--tab-icon) + 0.5rem);
		border-radius: var(--radius-pill);
	}

	.pill :global(.icon) {
		inline-size: var(--tab-icon);
		block-size: var(--tab-icon);
	}

	/* The indicator. It grows from its own centre, so it has no direction. */
	.pill::before {
		content: '';
		position: absolute;
		inset: 0;
		z-index: -1;
		border-radius: inherit;
		background-color: var(--nav-indicator);
		opacity: 0;
		transform: scaleX(0.6);
		transition:
			opacity 150ms linear,
			transform 250ms var(--ease-enter);
	}

	a[aria-current] {
		color: var(--accent);
		font-weight: 600;
	}

	/* The filled glyph's knocked-out strokes take the colour behind them. */
	a[aria-current] .pill {
		--icon-knockout: var(--nav-indicator);
	}

	a[aria-current] .pill::before {
		opacity: 1;
		transform: none;
	}

	@media (hover: hover) and (pointer: fine) {
		a:not([aria-current]):hover .pill::before {
			opacity: 0.4;
			transform: none;
		}
	}

	/* The press, and the held tint while the tapped tab's page loads
	   (`data-pending`, set and cleared by nav.svelte.ts). `:global()` because
	   the attribute is written from script, so the compiler never sees it in
	   this markup and would otherwise prune the rule as unused. Never on the
	   lit tab: its indicator is already full, and dimming it to the pressed
	   tint while its root loads read as the tab switching off. */
	a:not([aria-current]):active .pill::before,
	a:not([aria-current]):global([data-pending]) .pill::before {
		opacity: 0.6;
		transform: none;
		transition-duration: 0s;
	}

	a:focus-visible {
		outline-offset: -2px;
		border-radius: var(--radius-md);
	}
</style>
